import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';

// Helper to format the price
const formatPrice = (price) => {
  const num = parseInt(price, 10);
  if (isNaN(num)) return 'Price not available';
  return `UGX ${num.toLocaleString()}`;
};

// Helper to find a specific parameter
const findParameter = (parameters, name) => {
  const param = parameters?.find(p => p.name.toLowerCase() === name.toLowerCase());
  return param ? param.value : null;
};

const PropertyCard = ({ property, isFavorite, onToggleFavorite }) => {
  if (!property) return null;

  const {
    id,
    slug_id,
    title,
    price,
    address,
    property_type,
    title_image,
    is_premium,
    parameters,
    post_created,
  } = property;

  const bedrooms = findParameter(parameters, 'bedrooms');
  const bathrooms = findParameter(parameters, 'bathrooms');
  const area = findParameter(parameters, 'area') || findParameter(parameters, 'sqft');

  return (
    <article itemScope itemType="https://schema.org/Offer" className="bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 group">
      <div className="relative">
        <Link to={`/property/${slug_id}`} className="block" title={`View details for ${title}`} aria-label={`View details for ${title}`}>
          <img
            src={title_image}
            alt={`${title} - ${address}`}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            itemProp="image"
          />
        </Link>
        <meta itemProp="priceCurrency" content="UGX" />
        <meta itemProp="price" content={price || ''} />
        <meta itemProp="url" content={`https://hirebroker.co/property/${slug_id}`} />

        <div className="absolute top-2 left-2 flex flex-col gap-2">
          <Badge variant="secondary" className="capitalize">
            For {property_type}
          </Badge>
          {is_premium && (
            <Badge variant="premium">
              <Icon name="Star" className="mr-1" size={12} />
              Taken
            </Badge>
          )}
        </div>

        <div className="absolute top-2 right-2">
          <Button
            variant="ghost"
            size="icon"
            className="bg-background/60 hover:bg-background rounded-full"
            onClick={() => onToggleFavorite(id)}
            aria-label="Toggle Favorite"
          >
            <Icon
              name="Heart"
              className={`transition-colors ${isFavorite ? 'text-red-500 fill-current' : 'text-card-foreground'}`}
            />
          </Button>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-foreground truncate hover:text-primary">
          <Link to={`/property/${slug_id}`}>{title}</Link>
        </h3>

        <p className="text-sm text-muted-foreground truncate mt-1">
          <Icon name="MapPin" className="inline-block mr-1 -mt-1" size={14} />
          {address}
        </p>

        <div className="mt-4 text-lg font-bold text-primary">
          {formatPrice(price)}
        </div>

        <div className="mt-4 pt-4 border-t border-border flex justify-between items-center text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            {bedrooms && (
              <span className="flex items-center gap-1.5" title={`${bedrooms} Bedrooms`}>
                <Icon name="BedDouble" size={16} />
                {bedrooms}
              </span>
            )}
            {bathrooms && (
              <span className="flex items-center gap-1.5" title={`${bathrooms} Bathrooms`}>
                <Icon name="Bath" size={16} />
                {bathrooms}
              </span>
            )}
            {area && (
              <span className="flex items-center gap-1.5" title={`${area} sq ft`}>
                <Icon name="Square" size={16} />
                {area}
              </span>
            )}
          </div>
          <span className="text-xs">{post_created}</span>
        </div>
      </div>
    </article>
  );
};

export default PropertyCard;